<div class="flyout-audience-hub">
  <!-- Header -->
  <header class="hub-header">
    <div class="hub-header__icon">
      <mat-icon>{{ isExisting() ? "edit" : "groups" }}</mat-icon>
    </div>
    <label class="hub-header__name-label" for="hub-flyout-name">{{ "audience.settings.audience-name" | translate }}</label>
    <input
      #nameInput
      id="hub-flyout-name"
      class="hub-header__name"
      type="text"
      [value]="name()"
      (input)="onNameInput($event)"
      [placeholder]="'data-platform.audience-name-placeholder' | translate"
      [disabled]="isDefault()"
      data-test="hub-flyout-name" />
    <div class="hub-header__sources">
      @for (src of sources(); track src.kind) {
        <span class="source-chip">
          @if (src.logoUrl) {
            <img [src]="src.logoUrl" [alt]="src.label" class="source-chip__logo" />
          } @else {
            <span class="source-chip__logo source-chip__logo--internal">T</span>
          }
          <span>{{ src.label }}</span>
        </span>
      }
      @if (isDefault()) {
        <span class="default-pill">
          <mat-icon>lock</mat-icon>
          {{ "audience.default-audience-disabled" | translate }}
        </span>
      }
    </div>
    <button mat-icon-button class="hub-header__close" (click)="onCancel()" [attr.aria-label]="'generic.close' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <!-- Tabs -->
  <nav class="hub-tabs" role="tablist">
    <button
      class="hub-tab"
      [class.hub-tab--active]="activeTab() === 'build'"
      [attr.aria-selected]="activeTab() === 'build'"
      role="tab"
      (click)="setTab('build')">
      <mat-icon>rule</mat-icon>
      <span>{{ "data-platform.flyout-tab-build" | translate }}</span>
    </button>
    <button class="hub-tab" [class.hub-tab--active]="activeTab() === 'test'" [attr.aria-selected]="activeTab() === 'test'" role="tab" (click)="setTab('test')">
      <mat-icon>person_search</mat-icon>
      <span>{{ "data-platform.flyout-tab-test" | translate }}</span>
    </button>
    <button
      class="hub-tab"
      [class.hub-tab--active]="activeTab() === 'analytics'"
      [attr.aria-selected]="activeTab() === 'analytics'"
      role="tab"
      (click)="setTab('analytics')">
      <mat-icon>analytics</mat-icon>
      <span>{{ "data-platform.flyout-tab-analytics" | translate }}</span>
    </button>
  </nav>

  <!-- Body -->
  <main class="hub-body">
    <!-- BUILD TAB -->
    @if (activeTab() === "build") {
      <section class="hub-pane" data-test="hub-pane-build">
        <!-- Reach card — hidden for new audiences with no rules. -->
        @if (reachKind() !== "hidden") {
          <div class="reach-card" [class.reach-card--recalculating]="reachKind() === 'recalculating'">
            <div class="reach-card__label">{{ "data-platform.flyout-current-reach" | translate }}</div>
            @switch (reachKind()) {
              @case ("exact") {
                <div class="reach-card__value">
                  @if (isReachEstimate()) {
                    <span class="reach-card__tilde">~</span>
                  }
                  <span>{{ displayReach() | toLocaleNumber }}</span>
                </div>
                @if (isReachEstimate() && reachRange()) {
                  <div class="reach-card__range">
                    <span class="range-pill range-pill--estimate">
                      ~{{ reachRange()!.lower | toLocaleNumber }} – {{ reachRange()!.upper | toLocaleNumber }}
                    </span>
                  </div>
                } @else {
                  <div class="reach-card__sub" data-test="hub-reach-sub">
                    @if (isDefault() && audience().reachType === "Weekly") {
                      {{ "audience.reach.weekly" | translate }}
                    } @else if (reachPeriod(); as period) {
                      {{ period.key | translate: period.params }}
                    } @else {
                      {{ "data-platform.reach-all-matching" | translate }}
                      <mat-icon class="reach-card__info" [matTooltip]="'data-platform.reach-all-matching-hint' | translate">info</mat-icon>
                    }
                  </div>
                }
              }
              @case ("upper-bound") {
                <div class="reach-card__value">
                  <span class="reach-card__tilde reach-card__tilde--bound">≤</span>
                  <span>{{ displayReach() | toLocaleNumber }}</span>
                </div>
                <div class="reach-card__sub" data-test="hub-reach-sub">
                  {{ "data-platform.reach-base-audience" | translate }}
                  @if (reachPeriod(); as period) {
                    · {{ period.key | translate: period.params }}
                  }
                </div>
              }
              @case ("lower-bound") {
                <div class="reach-card__value">
                  <span class="reach-card__tilde reach-card__tilde--bound">≥</span>
                  <span>{{ displayReach() | toLocaleNumber }}</span>
                </div>
                <div class="reach-card__sub" data-test="hub-reach-sub" [matTooltip]="'data-platform.reach-lower-bound-note' | translate">
                  {{ "data-platform.reach-lower-bound-meta" | translate }}
                  @if (reachPeriod(); as period) {
                    · {{ period.key | translate: period.params }}
                  }
                </div>
              }
              @case ("recalculating") {
                <div class="reach-card__value reach-card__value--with-spinner">
                  <span>
                    @if (isReachEstimate()) {
                      <span class="reach-card__tilde">~</span>
                    }
                    {{ displayReach() | toLocaleNumber }}
                  </span>
                  <mat-spinner diameter="14"></mat-spinner>
                </div>
                <div class="reach-card__sub">{{ "audience.statistics.calculating" | translate }}</div>
              }
              @case ("loading") {
                <div class="reach-card__value reach-card__value--loading">
                  <mat-spinner diameter="20"></mat-spinner>
                </div>
                <div class="reach-card__sub">{{ "audience.statistics.calculating" | translate }}</div>
              }
              @case ("na") {
                <div class="reach-card__value reach-card__value--na">N/A</div>
                <div class="reach-card__sub">{{ "audience.cant-calculate-reach" | translate }}</div>
              }
            }

            <!-- "≤" note only when conditions genuinely narrow the count further
                 (i.e. the result is an upper bound). Promoted (New/Returning) and
                 estimated (device/country/…) conditions are already accounted for,
                 so they don't get this note. -->
            @if (reachKind() === "upper-bound" && hasConditions()) {
              <div class="reach-card__conditions-note">
                <mat-icon>bolt</mat-icon>
                <span>{{ "data-platform.reach-conditions-note" | translate }}</span>
              </div>
            }

            <!-- Estimated criteria applied (device / country / source / utm). -->
            @if (reachFactorNotes().length) {
              <div class="reach-card__estimate-note" [matTooltip]="'data-platform.reach-factor-approx-note' | translate">
                <mat-icon>insights</mat-icon>
                <span>{{ reachFactorNotes().join(" · ") }}</span>
              </div>
            }

            @if (isThirdPartyMix() && !isDefault()) {
              <div class="reach-card__estimate-note" data-test="hub-reach-third-party-note">
                <mat-icon>functions</mat-icon>
                <span>{{ "data-platform.flyout-estimate-body" | translate }}</span>
              </div>
              @if (reachBreakdown(); as bd) {
                <div class="reach-card__breakdown">
                  <span>{{ bd.internalCount | toLocaleNumber }} {{ "data-platform.flyout-reach-internal-label" | translate }}</span>
                  @if (bd.integrationLabel) {
                    <span class="reach-card__breakdown-sep">·</span>
                    <span>
                      @if (bd.integrationCount != null) {
                        {{ bd.integrationCount | toLocaleNumber }}
                      } @else {
                        N/A
                      }
                      {{ "data-platform.flyout-reach-integration-label" | translate: { label: bd.integrationLabel } }}
                    </span>
                  }
                </div>
              }
            }
          </div>
        }

        <!-- Targeting criteria — one unified list (profile rules + live conditions). -->
        <div class="rules-block">
          <div class="rules-block__label">
            {{ "data-platform.flyout-criteria-header" | translate }}
          </div>

          @if (isDefault()) {
            @if (filterChips().length === 0 && conditionChips().length === 0) {
              <div class="rules-block__empty">{{ "data-platform.no-rules" | translate }}</div>
            } @else {
              <div class="rules-block__chips">
                @for (chip of filterChips(); track chip) {
                  <span class="rule-chip">{{ chip }}</span>
                }
                @for (chip of conditionChips(); track chip) {
                  <span class="rule-chip rule-chip--condition">{{ chip }}</span>
                }
              </div>
            }
          } @else {
            @if (!hasAnyCriteria()) {
              <div class="rules-block__empty">{{ "data-platform.no-rules" | translate }}</div>
            } @else {
              <div class="rules-block__editors">
                @for (row of criteriaRows(); track row.trackKey; let gi = $index) {
                  @if (row.separatorKey) {
                    <div class="rules-block__and">
                      <span>{{ row.separatorKey | translate }}</span>
                    </div>
                  }
                  <div class="criterion-row">
                    @switch (row.kind) {
                      @case ("rule") {
                        <app-audience-rule-editor
                          [rule]="rules()[row.dataIndex]"
                          [floorRule]="row.span === 2 ? rules()[row.dataIndex + 1] : undefined"
                          [eventTypes]="eventTypes()"
                          [goalNames]="goalNames()"
                          [tags]="tags()"
                          [dimensionValuesByType]="dimensionValuesByType()"
                          [accountDimensions]="accountDimensions()"
                          [widgetList]="widgetList()"
                          [widgetEventTypes]="widgetEventTypes()"
                          [consentVersionOptions]="consentVersionOptions()"
                          [optionsLoading]="optionsLoading()"
                          [currency]="accountCurrency()"
                          [sharedTypeMenu]="addCriterionMenu"
                          (ruleChange)="updateRule(row.dataIndex, $event)"
                          (remove)="removeRule(row.dataIndex)"
                          (changeTypeRequested)="requestReplace('rule', row.dataIndex)"
                          (typeMenuOpened)="onPickerOpened()"></app-audience-rule-editor>
                      }
                      @case ("filter") {
                        <app-audience-inline-editor
                          [filter]="extraFilters()[row.dataIndex]"
                          [allWidgets]="widgetList()"
                          [accountDomain]="accountDomain()"
                          [sharedTypeMenu]="addCriterionMenu"
                          (filterChange)="updateExtraFilter(row.dataIndex, $event)"
                          (remove)="removeLegacyFilter(row.dataIndex)"
                          (changeTypeRequested)="requestReplace('filter', row.dataIndex)"
                          (typeMenuOpened)="onPickerOpened()"></app-audience-inline-editor>
                      }
                      @case ("condition") {
                        <app-audience-inline-editor
                          [condition]="conditions()[row.dataIndex]"
                          [allWidgets]="widgetList()"
                          [accountDomain]="accountDomain()"
                          [sharedTypeMenu]="addCriterionMenu"
                          (conditionChange)="updateCondition(row.dataIndex, $event)"
                          (remove)="removeCondition(row.dataIndex)"
                          (changeTypeRequested)="requestReplace('condition', row.dataIndex)"
                          (typeMenuOpened)="onPickerOpened()"></app-audience-inline-editor>
                      }
                    }
                    <span
                      class="criterion-badge criterion-badge--{{ row.badgeKind }}"
                      [matTooltip]="'data-platform.criteria-badge-tooltip-' + row.badgeKind | translate">
                      {{ row.badgeKey | translate }}
                    </span>
                  </div>
                }
              </div>
            }

            <!-- Combine operator — only when both profile rules and live conditions exist. -->
            @if (canChooseAudienceOperator()) {
              <div class="conditions-operator">
                <span class="conditions-operator__label">{{ "data-platform.criteria-combine-label" | translate }}</span>
                <mat-button-toggle-group
                  hideSingleSelectionIndicator
                  [value]="audienceOperator()"
                  (change)="setAudienceOperator($event.value)"
                  class="conditions-operator__toggle"
                  data-test="hub-audience-operator">
                  <mat-button-toggle [value]="AndOrOperator.And">{{ "generic.and" | translate | uppercase }}</mat-button-toggle>
                  <mat-button-toggle [value]="AndOrOperator.Or">{{ "generic.or" | translate | uppercase }}</mat-button-toggle>
                </mat-button-toggle-group>
              </div>
            }

            <!-- Single searchable picker — rules, pass-through filters and conditions. -->
            <button
              mat-stroked-button
              class="rules-block__add"
              [matMenuTriggerFor]="addCriterionMenu"
              (click)="requestAdd()"
              (menuOpened)="onPickerOpened()"
              data-test="hub-add-criterion">
              <mat-icon>add</mat-icon>
              <span>{{ "data-platform.add-criterion" | translate }}</span>
            </button>
            <mat-menu #addCriterionMenu="matMenu" class="rule-type-picker criteria-picker" xPosition="after" (closed)="onPickerClosed()">
              <div class="criteria-picker__search" (click)="$event.stopPropagation()" (keydown)="$event.key !== 'Escape' && $event.stopPropagation()">
                <mat-icon>search</mat-icon>
                <input
                  type="text"
                  class="criteria-picker__search-input"
                  [value]="criteriaSearch()"
                  (input)="onCriteriaSearchInput($event)"
                  [placeholder]="'data-platform.criteria-search-placeholder' | translate"
                  [attr.aria-label]="'data-platform.criteria-search-placeholder' | translate"
                  data-test="hub-criteria-search" />
              </div>
              <div class="criteria-picker__scroll">
                @for (group of filteredPickerGroups(); track group.label) {
                  <div class="rule-type-picker__group">
                    <div class="rule-type-picker__group-label">
                      {{ group.label }}
                      @if (group.unavailableKey) {
                        <span class="rule-type-picker__group-side rule-type-picker__group-side--unavailable" data-test="hub-picker-group-unavailable">
                          · {{ group.unavailableKey | translate }}
                        </span>
                      } @else {
                        <span
                          class="rule-type-picker__group-side"
                          [matTooltip]="'data-platform.criteria-badge-tooltip-' + group.side | translate"
                          matTooltipPosition="above"
                          matTooltipShowDelay="350">
                          · {{ "data-platform.criteria-side-" + group.side | translate }}
                        </span>
                      }
                    </div>
                    @for (item of group.items; track item.kind + ":" + item.dataTest) {
                      <button
                        mat-menu-item
                        class="rule-type-picker__item-btn"
                        [class.rule-type-picker__item-btn--locked]="item.isLocked"
                        [disabled]="!!group.unavailableKey"
                        (click)="onPickerSelect(item)"
                        [matTooltip]="item.descKey || '' | translate"
                        matTooltipPosition="right"
                        matTooltipShowDelay="350"
                        [attr.data-test]="item.dataTest">
                        @if (item.iconUrl) {
                          <img [src]="item.iconUrl" class="rule-type-picker__logo" [alt]="item.label" />
                        } @else {
                          <mat-icon>{{ item.icon || "tune" }}</mat-icon>
                        }
                        <span class="rule-type-picker__label">{{ item.label }}</span>
                        @if (item.showProTag) {
                          <span class="rule-type-picker__pro" [class.rule-type-picker__pro--has-access]="!item.isLocked">PRO</span>
                        }
                        @if (item.isNew) {
                          <span class="rule-type-picker__new">NEW</span>
                        }
                      </button>
                    }
                  </div>
                }
                @if (!pickerHasResults()) {
                  <div class="criteria-picker__empty">{{ "data-platform.criteria-search-empty" | translate }}</div>
                }
              </div>
            </mat-menu>
          }
        </div>
        @if (canShowGroupShare()) {
          <div class="group-share-block">
            <mat-checkbox
              class="group-share-block__checkbox"
              color="primary"
              [checked]="shareWithGroup()"
              [disabled]="!!audience().accountGroupId"
              (change)="onGroupShareChange($event.checked)">
              @if (!audience().accountGroupId) {
                {{ "audience.customer.enable" | translate }}
              } @else {
                {{ "audience.customer.available" | translate }}
              }
            </mat-checkbox>
            <div class="group-share-block__hint">
              <mat-icon>help_outline</mat-icon>
              @if (!audience().accountGroupId) {
                <span [innerHTML]="'audience.customer.title-text' | translate"></span>
              } @else {
                <span>{{ "audience.customer.available-hint" | translate }}</span>
              }
            </div>
          </div>
        }
      </section>
    }

    <!-- TEST TAB -->
    @if (activeTab() === "test") {
      <section class="hub-pane" data-test="hub-pane-test">
        <h3 class="hub-pane__title">{{ "data-platform.audience-evaluate-title" | translate }}</h3>
        <p class="hub-pane__sub">{{ "data-platform.flyout-test-help" | translate }}</p>

        <div class="test-inputs">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="test-inputs__field">
            <mat-label>{{ "data-platform.uid" | translate }}</mat-label>
            <input
              matInput
              type="text"
              [value]="testUid()"
              (input)="onTestUidInput($event)"
              [placeholder]="'data-platform.uid-placeholder' | translate"
              data-test="hub-test-uid" />
          </mat-form-field>
          <span class="test-inputs__or">{{ "data-platform.or" | translate }}</span>
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="test-inputs__field">
            <mat-label>{{ "data-platform.identifier" | translate }}</mat-label>
            <input
              matInput
              type="text"
              [value]="testIdentifier()"
              (input)="onTestIdentifierInput($event)"
              [placeholder]="'data-platform.identifier-placeholder' | translate"
              data-test="hub-test-identifier" />
          </mat-form-field>
        </div>

        <button mat-flat-button color="primary" (click)="runTest()" [disabled]="!canRunTest()" class="run-test-btn" data-test="hub-test-run">
          @if (testRunning()) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            <mat-icon>play_arrow</mat-icon>
          }
          <span>{{ "data-platform.run-evaluate" | translate }}</span>
        </button>

        @if (testResult(); as result) {
          <div class="test-result-card" [class.test-result-card--matched]="result.matched">
            <div class="test-result-card__header">
              <mat-icon class="test-result-card__status-icon">
                {{ result.matched ? "check_circle" : "cancel" }}
              </mat-icon>
              <div>
                <div class="test-result-card__headline">
                  {{ (result.matched ? "data-platform.matched" : "data-platform.not-matched") | translate }}
                </div>
                <div class="test-result-card__sub">
                  {{ (result.matched ? "data-platform.flyout-test-matched-info" : "data-platform.flyout-test-not-matched-info") | translate }}
                </div>
              </div>
            </div>
            @if (result.rows.length > 0) {
              <ul class="test-result-card__rows">
                @for (row of result.rows; track row.type) {
                  <li>
                    <span class="test-result-row__dot" [attr.data-state]="row.satisfied === true ? 'ok' : row.satisfied === false ? 'fail' : 'unknown'"></span>
                    <span>{{ row.type }}</span>
                  </li>
                }
              </ul>
            }
          </div>
        }
      </section>
    }

    <!-- ANALYTICS TAB -->
    @if (activeTab() === "analytics") {
      <section class="hub-pane" data-test="hub-pane-analytics">
        <h3 class="hub-pane__title">{{ "data-platform.flyout-tab-analytics" | translate }}</h3>
        <p class="hub-pane__sub">{{ "data-platform.flyout-analytics-help" | translate }}</p>

        <!-- Usage list -->
        <div class="usage-block">
          <div class="usage-block__label">
            {{ "data-platform.flyout-used-in-campaigns" | translate }}
          </div>
          @if (widgetsLoadState() === "loading") {
            <div class="usage-block__empty" data-test="hub-usage-loading">
              <mat-spinner diameter="16"></mat-spinner>
              {{ "generic.loading" | translate }}
            </div>
          } @else if (widgetsLoadState() === "failed") {
            <div class="usage-block__empty" data-test="hub-usage-failed">
              <mat-icon>error_outline</mat-icon>
              {{ "data-platform.audience-campaigns-load-failed" | translate }}
            </div>
          } @else if (usageRows().length === 0) {
            @if (otherAccountsCampaignCount() === 0) {
              <div class="usage-block__empty">
                <mat-icon>block</mat-icon>
                {{ "data-platform.not-used" | translate }}
              </div>
            }
          } @else {
            <ul class="usage-list">
              @for (row of usageRows(); track row.campaignId ?? row.campaignName) {
                <li class="usage-row" [class.usage-row--linked]="row.campaignId != null">
                  <a class="usage-row__link" [routerLink]="row.campaignId != null ? ['/campaigns', row.campaignId, 'settings'] : null">
                    <div class="usage-row__icon"><mat-icon>campaign</mat-icon></div>
                    <div class="usage-row__body">
                      <div class="usage-row__name">{{ row.campaignName }}</div>
                      @if (row.typeAndStatus) {
                        <div class="usage-row__type">{{ row.typeAndStatus }}</div>
                      }
                    </div>
                    @if (row.campaignId != null) {
                      <mat-icon class="usage-row__arrow">arrow_forward</mat-icon>
                    }
                  </a>
                </li>
              }
            </ul>
          }
          @if (otherAccountsCampaignCount() > 0) {
            <div class="usage-block__empty usage-block__other-accounts" data-test="hub-usage-other-accounts">
              <mat-icon>domain</mat-icon>
              {{ "data-platform.flyout-used-on-other-accounts" | translate: { count: otherAccountsCampaignCount() } }}
            </div>
          }
        </div>

        @if (canShowTrendChart()) {
          <app-audience-trend-chart
            [audienceId]="trendAudienceId()"
            [siteIds]="trendSiteIds()"
            [rules]="trendRules()"
            [estimateShare]="trendEstimateShare()"
            [featurePage]="featurePage()"></app-audience-trend-chart>
        } @else if (trendResolving()) {
          <div class="trend-placeholder trend-placeholder--loading">
            <mat-spinner diameter="24"></mat-spinner>
          </div>
        } @else {
          <div class="trend-placeholder">
            <mat-icon>show_chart</mat-icon>
            <div>
              <div class="trend-placeholder__title">{{ "data-platform.flyout-trend-unavailable-title" | translate }}</div>
              <div class="trend-placeholder__sub">
                {{ (trendUnsupported() ? "data-platform.flyout-trend-unsupported-sub" : "data-platform.flyout-trend-unavailable-sub") | translate }}
              </div>
            </div>
          </div>
        }
      </section>
    }
  </main>

  <!-- Footer -->
  <footer class="hub-footer">
    @if (isExisting() && !isDefault() && isRemovable()) {
      <!-- disabledInteractive buttons still fire clicks, so isDisabled also has to stop the confirm dialog. -->
      <button
        mat-stroked-button
        color="warn"
        disabledInteractive
        deleteDialog
        [deletionType]="'data-platform.audience' | translate | lowercase"
        [deletionTypeText]="audience().name"
        deletionTypeHeader="confirmationDialog.confirmationTitle"
        deletionText="confirmationDialog.delete-audience"
        [disabled]="deleteDisabled()"
        [isDisabled]="deleteDisabled()"
        [matTooltip]="deleteBlockedReason()"
        matTooltipClass="multiline-tooltip long-value-tooltip"
        (onDelete)="onDelete()"
        data-test="hub-delete">
        @if (deleting()) {
          <mat-spinner diameter="16"></mat-spinner>
        } @else {
          <mat-icon fontSet="material-symbols-outlined">delete</mat-icon>
        }
        <span>{{ "audience.delete-audience" | translate }}</span>
      </button>
    }
    <div class="hub-footer__spacer"></div>
    <button mat-stroked-button class="secondary-button" (click)="onCancel()">{{ "generic.cancel" | translate }}</button>
    <button mat-flat-button color="primary" (click)="onSave()" [disabled]="saving() || deleting() || !canSave()" data-test="hub-save">
      @if (saving()) {
        <mat-spinner diameter="16"></mat-spinner>
      } @else {
        <mat-icon>save</mat-icon>
      }
      <span>{{ "generic.save" | translate }}</span>
    </button>
  </footer>
</div>
